import React, { useState, useEffect, useRef } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Lock, Mail, AlertCircle, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import MfaSetup from './MfaSetup';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [tempToken, setTempToken] = useState<string | null>(null);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [trustDevice, setTrustDevice] = useState(true);

  // Use ref to preserve email across renders
  const emailRef = useRef<string>('');

  const { login, verifyMfaLogin, isAuthenticated, error, clearError } = useAuth();
  const location = useLocation();

  // Get the intended destination from state
  const from = location.state?.from?.pathname || '/dashboard';

  // Clear error when component mounts
  useEffect(() => {
    clearError();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep email ref in sync
  useEffect(() => {
    emailRef.current = email;
  }, [email]);

  // Prevent MFA state from being reset on error - preserve state if MFA is required
  useEffect(() => {
    // If we have MFA required but somehow mfaRequired got reset, restore it
    // This is a defensive measure to prevent state loss
    if (tempToken && !mfaRequired && !mfaSetupRequired && !showMfaSetup) {
      // Don't auto-restore - let user explicitly trigger
      console.warn('MFA state inconsistency detected - tempToken exists but MFA not required');
    }
  }, [tempToken, mfaRequired, mfaSetupRequired, showMfaSetup]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    clearError(); // Clear any previous errors

    // Store email in a ref or ensure it's preserved
    const currentEmail = email || emailRef.current;
    console.log('handleSubmit - currentEmail:', currentEmail, 'email state:', email, 'emailRef:', emailRef.current);

    // Handle MFA verification separately - don't let errors propagate
    if (mfaRequired && tempToken) {
      // Verify MFA code
      try {
        await verifyMfaLogin(email, mfaCode, tempToken, trustDevice);
        // Navigation will be handled by the redirect logic above
        // If successful, user will be authenticated and redirected
        return;
      } catch (mfaError: any) {
        // MFA verification failed - stay on MFA form, don't reset state
        console.error('MFA verification failed:', mfaError);
        console.log('Preserving MFA state - mfaRequired:', mfaRequired, 'tempToken:', !!tempToken);
        
        // Explicitly preserve MFA state - ensure it doesn't get reset
        if (!mfaRequired) {
          console.warn('MFA state was reset, restoring...');
          setMfaRequired(true);
        }
        if (!tempToken) {
          console.warn('Temp token was lost, cannot restore MFA state');
        }
        
        setIsLoading(false);
        // Clear the MFA code input so user can try again
        setMfaCode('');
        // Error message will be shown by the auth context
        // Keep mfaRequired and tempToken so user can try again
        // Explicitly prevent any state reset
        return;
      }
    }
    
    // Initial login attempt
    try {
        // Initial login
        console.log('Attempting login for:', currentEmail);
        const response = await login(currentEmail, password) as { mfaRequired?: boolean; mfaSetupRequired?: boolean; tempToken?: string };
        console.log('LoginPage received response:', response);
        console.log('mfaSetupRequired:', response?.mfaSetupRequired);
        console.log('mfaRequired:', response?.mfaRequired);
        console.log('Current email state:', email);
        
        // Check if MFA setup is required
        if (response?.mfaSetupRequired) {
          console.log('MFA setup required - setting state');
          console.log('Email at setup time:', currentEmail);
          console.log('Current email state:', email);
          
          // Always ensure email is set - batch state updates
          const emailToUse = currentEmail || emailRef.current;
          if (emailToUse) {
            if (email !== emailToUse) {
              console.log('Setting email state to:', emailToUse);
              setEmail(emailToUse);
            }
            emailRef.current = emailToUse;
          }
          
          // Set MFA states
          setMfaSetupRequired(true);
          setShowMfaSetup(true);
          setIsLoading(false); // Stop loading to show the setup UI
          
          console.log('State set - mfaSetupRequired: true, showMfaSetup: true, email will be:', emailToUse);
          return; // Return early to prevent further processing
        } 
        // Check if MFA verification is required (MFA already set up)
        else if (response?.mfaRequired && response?.tempToken) {
          console.log('Setting MFA required state');
          setMfaRequired(true);
          setTempToken(response.tempToken);
          setIsLoading(false); // Stop loading to show the MFA code input
          return; // Return early to prevent further processing
        }
        // If no MFA required, navigation will be handled by the redirect logic above
        console.log('No MFA required, login successful');
    } catch (error: any) {
      console.error('LoginPage error:', error);
      console.error('Error message:', error?.message);
      // Error is handled by the auth context, but we should still show the form
      setIsLoading(false);
    }
  };

  const handleMfaSetupComplete = async () => {
    // After MFA setup, user is automatically logged in by the backend
    // The auth context should handle the redirect
    setShowMfaSetup(false);
    setMfaSetupRequired(false);
  };

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <motion.div
          className="login-card"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header */}
          <motion.div className="login-header" variants={itemVariants}>
            <div className="login-logo">
              <div className="logo-icon">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="32" height="32" rx="8" fill="#2563eb"/>
                  <path d="M16 8V24M8 16H24" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="16" cy="16" r="2" fill="white"/>
                </svg>
              </div>
              <h1>CLIA Laboratory Analytics</h1>
            </div>
            <p className="login-subtitle">
              Sign in to access your laboratory dashboard
            </p>
          </motion.div>

          {/* Error Message */}
          {error && (
            <motion.div 
              className="error-message"
              variants={itemVariants}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <AlertCircle size={20} />
              <span>{error}</span>
            </motion.div>
          )}

          {/* Show MFA Setup if required */}
          {(showMfaSetup || mfaSetupRequired) ? (
            <motion.div 
              initial={{ opacity: 1, y: 0 }}
              animate={{ opacity: 1, y: 0 }}
              style={{ width: '100%', minHeight: '400px', display: 'flex', flexDirection: 'column', gap: '1rem' }}
            >
              <div className="mfa-setup-required-message">
                <Shield size={32} />
                <h2>MFA Setup Required</h2>
                <p>Multi-factor authentication is required for your account. Please set up MFA to continue.</p>
              </div>
              {(email || emailRef.current) ? (
                <div style={{ marginTop: '1rem' }}>
                  <MfaSetup 
                    email={email || emailRef.current}
                    onComplete={handleMfaSetupComplete}
                    onCancel={() => {
                      setShowMfaSetup(false);
                      setMfaSetupRequired(false);
                      clearError();
                    }}
                  />
                </div>
              ) : (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#e74c3c', background: '#fef2f2', borderRadius: '8px', margin: '1rem 0', border: '1px solid #fecaca' }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Email is required for MFA setup</p>
                  <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>Please refresh the page and try again.</p>
                  <p style={{ fontSize: '0.8rem', marginTop: '0.5rem', color: '#6b7280' }}>
                    Debug: showMfaSetup={String(showMfaSetup)}, mfaSetupRequired={String(mfaSetupRequired)}, email={email || 'missing'}
                  </p>
                </div>
              )}
            </motion.div>
          ) : (
            <>
              {/* Login Form */}
              <motion.form className="login-form" onSubmit={handleSubmit} variants={itemVariants}>
                {!mfaRequired ? (
                <>
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <div className="input-container">
                    <Mail className="input-icon" size={20} />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <div className="input-container">
                    <Lock className="input-icon" size={20} />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div className="form-options">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      disabled={isLoading}
                    />
                    <span className="checkmark"></span>
                    Remember me
                  </label>
                  <Link to="/forgot-password" className="forgot-password">
                    Forgot password?
                  </Link>
                </div>

                <motion.button
                  type="submit"
                  className="login-button"
                  disabled={isLoading || !email || !password}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isLoading ? (
                    <>
                      <div className="button-spinner"></div>
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </motion.button>
              </>
            ) : (
              <>
                <div className="mfa-prompt">
                  <p>Enter the 6-digit code from your authenticator app</p>
                </div>
                <div className="form-group">
                  <label htmlFor="mfaCode" style={{
                    display: 'block',
                    marginBottom: '0.75rem',
                    fontWeight: '600',
                    fontSize: '1rem',
                    color: '#1e293b',
                    letterSpacing: '0.025em'
                  }}>Verification Code</label>
                  <div className="input-container">
                    <Lock className="input-icon" size={20} />
                    <input
                      id="mfaCode"
                      type="text"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      required
                      disabled={isLoading}
                      maxLength={6}
                      pattern="[0-9]{6}"
                      style={{
                        fontSize: '1.25rem',
                        fontWeight: '600',
                        letterSpacing: '0.5em',
                        textAlign: 'center',
                        padding: '1rem 1.25rem',
                        border: '2px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '12px',
                        transition: 'all 0.3s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#3b82f6';
                        e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                </div>

                <div className="form-options" style={{ marginBottom: '1rem' }}>
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                    <input
                      type="checkbox"
                      checked={trustDevice}
                      onChange={(e) => setTrustDevice(e.target.checked)}
                      disabled={isLoading}
                    />
                    <span>Remember this device for 30 days</span>
                  </label>
                </div>

                <motion.button
                  type="submit"
                  className="login-button"
                  disabled={isLoading || mfaCode.length !== 6}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isLoading ? (
                    <>
                      <div className="button-spinner"></div>
                      Verifying...
                    </>
                  ) : (
                    'Verify Code'
                  )}
                </motion.button>

                <button
                  type="button"
                  className="back-to-login-button"
                  onClick={() => {
                    setMfaRequired(false);
                    setMfaCode('');
                    setTempToken(null);
                    clearError();
                  }}
                  disabled={isLoading}
                >
                  Back to Login
                </button>
                </>
              )}
              </motion.form>

              {/* Footer */}
              <motion.div className="login-footer" variants={itemVariants}>
            <p>
              Don't have an account?{' '}
              <Link to="/register" className="register-link">
                Contact your administrator
              </Link>
            </p>
            
            {/* Clinical Squared Branding */}
            <div className="clinical-squared-branding">
              <p className="powered-by">
                Powered by{' '}
                <a 
                  href="https://clinicalsquared.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="clinical-squared-link"
                >
                  Clinical Squared
                </a>
              </p>
            </div>
          </motion.div>
            </>
          )}

        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
